import { useCallback, useEffect, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const ROW_H = 40;
const LIST_H = 400;
const PAGE = 100;

// Virtualized, cursor-paginated list of the messages inside a bucket, with hand-pick delete.
export default function MessageDrilldown({ open, onOpenChange, accountId, filter, onDeleted }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const loadingRef = useRef(false);

  const fetchPage = useCallback(
    async (after) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const { data } = await api.post(`/cleanup/accounts/${accountId}/messages`, {
          filter,
          cursor: after || undefined,
          limit: PAGE,
        });
        setItems((cur) => (after ? [...cur, ...data.items] : data.items));
        setCursor(data.next_cursor);
        setHasMore(Boolean(data.next_cursor));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load messages");
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [accountId, filter]
  );

  useEffect(() => {
    if (!open) return;
    setItems([]);
    setPicked(new Set());
    setCursor(null);
    fetchPage(null);
  }, [open, fetchPage]);

  const toggle = (id) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const deletePicked = async () => {
    setDeleting(true);
    try {
      // Hand-picked IDs are capped at 1000 per job by the API.
      const ids = [...picked];
      for (let i = 0; i < ids.length; i += 1000) {
        await api.post("/cleanup/jobs", { account_id: accountId, filter: { email_ids: ids.slice(i, i + 1000) } });
      }
      toast.success(`Deleting ${ids.length} message${ids.length > 1 ? "s" : ""}`);
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start deletion job");
    } finally {
      setDeleting(false);
    }
  };

  const Row = ({ index, style }) => {
    const m = items[index];
    if (!m) return <div style={style} className="px-3 text-xs text-muted-foreground flex items-center">Loading…</div>;
    return (
      <label style={style} className="flex items-center gap-3 px-3 border-b border-border cursor-pointer hover:bg-secondary/40">
        <Checkbox checked={picked.has(m.email_id)} onCheckedChange={() => toggle(m.email_id)} />
        <span className="w-40 truncate text-sm">{m.sender_name || m.sender_email}</span>
        <span className="flex-1 min-w-0 truncate text-sm text-muted-foreground">{m.subject}</span>
        <span className="font-mono text-xs text-muted-foreground">{(m.received_at || "").slice(0, 10)}</span>
      </label>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="drilldown-dialog">
        <DialogHeader>
          <DialogTitle>Messages in selection</DialogTitle>
          <DialogDescription>Pick individual messages to delete. Newest first.</DialogDescription>
        </DialogHeader>
        <div className="border border-border" data-testid="drilldown-list">
          {items.length === 0 && !loading && <p className="p-4 text-sm text-muted-foreground">No messages.</p>}
          <List
            height={LIST_H}
            width="100%"
            itemCount={hasMore ? items.length + 1 : items.length}
            itemSize={ROW_H}
            onItemsRendered={({ visibleStopIndex }) => {
              if (hasMore && visibleStopIndex >= items.length - 5) fetchPage(cursor);
            }}
          >
            {Row}
          </List>
        </div>
        <div className="flex items-center justify-between text-xs font-mono">
          <span>{picked.size} picked · {items.length} loaded{hasMore ? "+" : ""}</span>
          <Button
            size="sm"
            variant="destructive"
            disabled={!picked.size || deleting}
            onClick={deletePicked}
            data-testid="drilldown-delete"
          >
            {deleting ? "Starting…" : `Delete ${picked.size}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
